import React, { useCallback, useEffect, useMemo, useState } from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  FormControl,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import axios from "api";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import dayjs from "dayjs";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { formatMoney } from "utils/money";
import { useSelector } from "react-redux";
import { RootState } from "store";

type DatePreset = "week" | "month" | "year" | "custom" | "all";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface TransactionRow {
  id: number;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  order_id: string | null;
  payment_id: string | null;
  created_at: string | null;
  plan_name: string | null;
  plan_description: string | null;
  amount: number | string | null;
  total_amount?: number | string | null;
  currency: string | null;
  status?: string | null;
  asset_name: string | null;
  asset_type_name: string | null;
}

interface AssetTypeOption {
  asset_type_id: number;
  asset_type_name: string;
}

interface CustomerOption {
  customer_id: number;
  name: string;
}

export interface TransactionsListProps {
  /** Render without dashboard chrome (e.g. inside customer slide-over). */
  embedded?: boolean;
  /** Lock list to one customer (vendor manage-customers menu). */
  fixedCustomerId?: number;
}

const TransactionsList: React.FC<TransactionsListProps> = ({
  embedded = false,
  fixedCustomerId,
}) => {
  const { fetchError } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const appCurrency =
    (user as any)?.client_currency ||
    (user as any)?.currency_code ||
    "USD";
  const [rows, setRows] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [assetTypes, setAssetTypes] = useState<AssetTypeOption[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [assetTypeFilter, setAssetTypeFilter] = useState<AssetTypeOption | null>(null);
  const [customerFilter, setCustomerFilter] = useState<CustomerOption | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'all' | 'success' | 'failed'>('all');
  const paymentStatusOptions = useMemo(
    () => [
      { label: "All statuses", value: "all" as const },
      { label: "Success", value: "success" as const },
      { label: "Failed / Missed", value: "failed" as const },
    ],
    []
  );
  const [datePreset, setDatePreset] = useState<DatePreset>("month");
  const [rangeStart, setRangeStart] = useState(() =>
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [rangeEnd, setRangeEnd] = useState(() =>
    dayjs().endOf("month").format("YYYY-MM-DD")
  );

  const dateQuery = useMemo(() => {
    if (datePreset === "all") return {};
    if (datePreset === "custom") {
      return { startDate: rangeStart, endDate: rangeEnd };
    }
    if (datePreset === "week") {
      return {
        startDate: dayjs().startOf("week").format("YYYY-MM-DD"),
        endDate: dayjs().endOf("week").format("YYYY-MM-DD"),
      };
    }
    if (datePreset === "year") {
      return {
        startDate: dayjs().startOf("year").format("YYYY-MM-DD"),
        endDate: dayjs().endOf("year").format("YYYY-MM-DD"),
      };
    }
    return {
      startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
      endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
    };
  }, [datePreset, rangeStart, rangeEnd]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {};
      if ("startDate" in dateQuery && dateQuery.startDate && "endDate" in dateQuery && dateQuery.endDate) {
        params.startDate = dateQuery.startDate;
        params.endDate = dateQuery.endDate;
      }
      if (assetTypeFilter?.asset_type_id) {
        params.assetTypeId = assetTypeFilter.asset_type_id;
      }
      if (customerFilter?.customer_id) {
        params.customerId = customerFilter.customer_id;
      }
      if (paymentStatus !== "all") {
        params.paymentStatus = paymentStatus === "failed" ? "failed" : "success";
      }
      const res = await axios.get("/customer-subscriptions/transactions", {
        params,
      });
      setRows(res.data?.transactions ?? []);
    } catch (err: any) {
      fetchError(
        err?.response?.data?.error || "Unable to load transactions right now."
      );
    } finally {
      setLoading(false);
    }
  }, [assetTypeFilter, customerFilter, dateQuery, fetchError, paymentStatus]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [assetRes, customerRes] = await Promise.all([
        axios.get("/asset-types"),
        axios.get("/customer"),
      ]);
      setAssetTypes(assetRes.data?.list ?? []);
      setCustomers(customerRes.data?.list ?? []);
    } catch {
      setAssetTypes([]);
      setCustomers([]);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    if (fixedCustomerId == null || !Number.isFinite(fixedCustomerId)) {
      return;
    }
    const match = customers.find((c) => c.customer_id === fixedCustomerId);
    setCustomerFilter(
      match ?? { customer_id: fixedCustomerId, name: "Customer" }
    );
  }, [customers, fixedCustomerId]);

  const clearFilters = () => {
    setAssetTypeFilter(null);
    if (fixedCustomerId == null) {
      setCustomerFilter(null);
    }
    setPaymentStatus("all");
    setDatePreset("month");
    setRangeStart(dayjs().startOf("month").format("YYYY-MM-DD"));
    setRangeEnd(dayjs().endOf("month").format("YYYY-MM-DD"));
  };

  const filterTextFieldSx = {
    "& .MuiInputBase-input": { color: "text.primary" },
    "& .MuiInputBase-input::placeholder": {
      color: "text.secondary",
      opacity: 1,
    },
    "& .MuiSvgIcon-root": { color: "text.secondary" },
  } as const;

  const handleDownloadInvoice = useCallback(
    async (transactionId?: number | null) => {
      if (!transactionId) {
        fetchError("Invoice not available for this transaction.");
        return;
      }
      try {
        const res = await axios.get(
          `/customer-subscriptions/transactions/${transactionId}/invoice`,
          {
            responseType: "blob",
          }
        );
        const rawContentType = res.headers["content-type"];
        const contentType =
          typeof rawContentType === "string"
            ? rawContentType
            : Array.isArray(rawContentType) && typeof rawContentType[0] === "string"
              ? rawContentType[0]
              : "application/pdf";
        const disposition = res.headers["content-disposition"];
        const blob = new Blob([res.data], { type: contentType });
        let downloadName = `customer-invoice-${transactionId}.pdf`;
        if (disposition) {
          const match = disposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
          if (match?.[1]) {
            const raw = match[1].replace(/"/g, "");
            try {
              downloadName = decodeURIComponent(raw);
            } catch {
              downloadName = raw;
            }
          }
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (err: any) {
        fetchError(
          err?.response?.data?.error || "Could not download the invoice file."
        );
      }
    },
    [fetchError]
  );

  const paidSummary = useMemo(() => {
    const paidRows = rows.filter(
      (row) => String(row?.status || "").trim().toUpperCase() === "PAID"
    );
    const currencyFromRows =
      paidRows.find((row) => row?.currency)?.currency || appCurrency || "USD";
    const totalValue = paidRows.reduce((sum, row) => {
      const raw = row?.total_amount ?? row?.amount ?? 0;
      const numeric = Number(raw);
      return Number.isFinite(numeric) ? sum + numeric : sum;
    }, 0);
    return {
      amount: totalValue,
      currency: currencyFromRows,
      count: paidRows.length,
    };
  }, [appCurrency, rows]);

interface AmountCellProps {
  row: TransactionRow;
  appCurrency: string;
}

const AmountCell: React.FC<AmountCellProps> = ({ row, appCurrency }) => {
  const value =
    row?.total_amount != null ? row.total_amount : row?.amount ?? 0;
  const rowCurrency = row?.currency || appCurrency || "USD";
  return (
    <Typography component="span">
      {formatMoney(Number(value ?? 0), String(rowCurrency))}
    </Typography>
  );
};

interface InvoiceCellProps {
  row: TransactionRow;
  onDownload: (id?: number | null) => void;
}

const InvoiceCell: React.FC<InvoiceCellProps> = ({ row, onDownload }) => {
  const id = row?.id;
  const isPaid = String(row?.status || "").toUpperCase() === "PAID";
  return (
    <IconButton
      aria-label="download invoice"
      size="small"
      disabled={!id || !isPaid}
      onClick={() => onDownload(id)}
    >
      <DownloadOutlinedIcon fontSize="small" />
    </IconButton>
  );
};

  const columns: MUIDataTableColumn[] = useMemo(
    () => [
      {
        name: "customer_name",
        label: "Customer",
        options: {
          customBodyRender: (value) => value || "—",
          setCellProps: () => ({
            style: { whiteSpace: "normal", wordBreak: "break-word", maxWidth: 160 },
          }),
        },
      },
      {
        name: "asset_type_name",
        label: "Asset Type",
        options: {
          customBodyRender: (value) => value || "—",
          setCellProps: () => ({
            style: { whiteSpace: "normal", wordBreak: "break-word", maxWidth: 140 },
          }),
        },
      },
      {
        name: "asset_name",
        label: "Asset",
        options: {
          customBodyRender: (value) => value || "—",
          setCellProps: () => ({
            style: { whiteSpace: "normal", wordBreak: "break-word", maxWidth: 160 },
          }),
        },
      },
      {
        name: "order_id",
        label: "Order ID",
        options: {
          customBodyRender: (value) => value || "—",
        },
      },
      {
        name: "payment_id",
        label: "Payment ID",
        options: {
          customBodyRender: (value) => value || "—",
        },
      },
      {
        name: "created_at",
        label: "Payment Date",
        options: {
          customBodyRender: (value) =>
            value ? dayjs(value).format("MMM D, YYYY HH:mm") : "—",
          setCellProps: () => ({
            style: { whiteSpace: "nowrap" },
          }),
        },
      },
      {
        name: "plan_name",
        label: "Subscription",
        options: {
          customBodyRender: (value) => value || "—",
          setCellProps: () => ({
            style: { whiteSpace: "normal", wordBreak: "break-word", maxWidth: 160 },
          }),
        },
      },
      {
        name: "currency",
        label: "Currency",
        options: {
          display: false,
          filter: false,
          sort: false,
        },
      },
      {
        name: "amount",
        label: "Amount",
        options: {
          customBodyRenderLite: (dataIndex) => (
            <AmountCell
              row={rows[dataIndex]}
              appCurrency={appCurrency}
            />
          ),
          setCellProps: () => ({
            style: { textAlign: "right" },
          }),
        },
      },
      {
        name: "id",
        label: "Invoice",
        options: {
          sort: false,
          filter: false,
          customBodyRenderLite: (dataIndex) => (
            <InvoiceCell
              row={rows[dataIndex]}
              onDownload={handleDownloadInvoice}
            />
          ),
        },
      },
    ],
    [handleDownloadInvoice, rows, appCurrency]
  );

const tableOptions: MUIDataTableOptions = useMemo(
    () => ({
      selectableRows: "none",
      selectableRowsHeader: false,
      elevation: 0,
      tableBodyHeight: "auto",
      responsive: "standard",
      search: true,
      download: false,
      print: false,
      textLabels: {
        body: {
          noMatch: loading
            ? "Loading customer transactions..."
            : "No transactions found.",
        },
      },
    }),
    [loading]
  );

  const inner = (
    <Card
      sx={{
        p: 2,
        mb: embedded ? 0 : 4,
        maxWidth: embedded ? "none" : 1400,
        mx: embedded ? 0 : "auto",
        boxShadow: embedded ? "none" : undefined,
      }}
    >
        <CardContent sx={{ "&:last-child": { pb: embedded ? 2 : undefined } }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            Customer Transactions
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "space-between",
              alignItems: "flex-end",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "flex-end",
                flex: "1 1 500px",
                minWidth: 280,
              }}
            >
            <FormControl sx={{ width: 200 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ mb: 0.5 }}
              >
                Asset Type
              </Typography>
              <Autocomplete
                options={assetTypes}
                getOptionLabel={(option) => option.asset_type_name ?? ""}
                value={assetTypeFilter}
                onChange={(_event, value) => setAssetTypeFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All asset types"
                    size="small"
                    sx={filterTextFieldSx}
                  />
                )}
              />
            </FormControl>
             <FormControl sx={{ width: 200 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ mb: 0.5 }}
              >
                Status
              </Typography>
              <Autocomplete
                options={paymentStatusOptions}
                size="small"
                getOptionLabel={(option) => option.label}
                value={
                  paymentStatusOptions.find(
                    (option) => option.value === paymentStatus
                  ) ?? paymentStatusOptions[0]
                }
                onChange={(_event, value) =>
                  setPaymentStatus(value?.value ?? "all")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All statuses"
                    size="small"
                    sx={filterTextFieldSx}
                  />
                )}
              />
            </FormControl>
            {fixedCustomerId == null && (
              <FormControl sx={{ width: 200 }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ mb: 0.5 }}
                >
                  Customer
                </Typography>
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) => option.name ?? ""}
                  value={customerFilter}
                  onChange={(_event, value) => setCustomerFilter(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="All customers"
                      size="small"
                      sx={filterTextFieldSx}
                    />
                  )}
                />
              </FormControl>
            )}
           
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <ButtonGroup size="small" variant="outlined">
                <Button
                  onClick={() => setDatePreset("week")}
                  variant="outlined"
                  color={datePreset === "week" ? "primary" : "secondary"}
                  sx={{
                    fontWeight: datePreset === "week" ? 600 : 400,
                    color: datePreset === "week" ? "primary.main" : "text.primary",
                    ...(datePreset === "week" ? { borderWidth: 2 } : {}),
                  }}
                >
                  This week
                </Button>
                <Button
                  onClick={() => setDatePreset("month")}
                  variant="outlined"
                  color={datePreset === "month" ? "primary" : "secondary"}
                  sx={{
                    fontWeight: datePreset === "month" ? 600 : 400,
                    color: datePreset === "month" ? "primary.main" : "text.primary",
                    ...(datePreset === "month" ? { borderWidth: 2 } : {}),
                  }}
                >
                  This month
                </Button>
                <Button
                  onClick={() => setDatePreset("year")}
                  variant="outlined"
                  color={datePreset === "year" ? "primary" :"secondary"}
                  sx={{
                    fontWeight: datePreset === "year" ? 600 : 400,
                    color: datePreset === "year" ? "primary.main" : "text.primary",
                    ...(datePreset === "year" ? { borderWidth: 2 } : {}),
                  }}
                >
                  This year
                </Button>
                <Button
                  onClick={() => setDatePreset("all")}
                  variant="outlined"
                  color={datePreset === "all" ? "primary" : "secondary"}
                  sx={{
                    fontWeight: datePreset === "all" ? 600 : 400,
                    color: datePreset === "all" ? "primary.main" : "text.primary",
                    ...(datePreset === "all" ? { borderWidth: 2 } : {}),
                  }}
                >
                  All
                </Button>
                <Button
                  onClick={() => setDatePreset("custom")}
                  variant="outlined"
                  color={datePreset === "custom" ? "primary" : "secondary"}
                  sx={{
                    fontWeight: datePreset === "custom" ? 600 : 400,
                    color: datePreset === "custom" ? "primary.main" : "text.primary",
                    ...(datePreset === "custom" ? { borderWidth: 2 } : {}),
                  }}
                >
                  Custom
                </Button>
              </ButtonGroup>
              {datePreset === "custom" && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "flex-end",
                  }}
                >
                  <FormControl sx={{ minWidth: 160 }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ pb: 0.75, display: "block" }}
                    >
                      From
                    </Typography>
                    <TextField
                      size="small"
                      type="date"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                      variant="outlined"
                    />
                  </FormControl>
                  <FormControl sx={{ minWidth: 160 }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ pb: 0.75, display: "block" }}
                    >
                      To
                    </Typography>
                    <TextField
                      size="small"
                      type="date"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      variant="outlined"
                    />
                  </FormControl>
                </Box>
              )}
            </Box>
              {(assetTypeFilter ||
                (fixedCustomerId == null && customerFilter) ||
                datePreset !== "month") && (
                <Button variant="outlined" color="primary" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "baseline",
                gap: 1,
                ml: "auto",
                minWidth: 220,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Total Amount:
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {formatMoney(
                  paidSummary.amount,
                  String(paidSummary.currency || appCurrency)
                )}
              </Typography>
            </Box>
          </Box>
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={undefined}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
        </CardContent>
      </Card>
  );

  if (embedded) {
    return inner;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {inner}
    </DashboardLayout>
  );
};

export default TransactionsList;
